import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { DataGrid } from '@mui/x-data-grid';
import EducatorLayout from './components/EducatorLayout';
import educatorAnalyticsApi from '../api/educatorAnalytics';

const buildDownloadUrl = (path) => {
  const base = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
  if (!base) return `/api${path}`;
  return `${base.replace(/\/$/, '')}${path}`;
};

const EducatorAnalytics = () => {
  const [lessonEngagement, setLessonEngagement] = useState({ summary: {}, rows: [] });
  const [assessmentStats, setAssessmentStats] = useState({ summary: {}, rows: [] });
  const [atRisk, setAtRisk] = useState({ summary: {}, rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [lessons, assessments, risk] = await Promise.all([
          educatorAnalyticsApi.getLessonEngagement(),
          educatorAnalyticsApi.getAssessmentStats(),
          educatorAnalyticsApi.getAtRisk(),
        ]);
        setLessonEngagement(lessons || { summary: {}, rows: [] });
        setAssessmentStats(assessments || { summary: {}, rows: [] });
        setAtRisk(risk || { summary: {}, rows: [] });
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const lessonColumns = [
    { field: 'title', headerName: 'Lesson', minWidth: 200, flex: 1 },
    { field: 'subject', headerName: 'Subject', minWidth: 140 },
    { field: 'grade', headerName: 'Grade', minWidth: 90 },
    { field: 'section', headerName: 'Section', minWidth: 120 },
    { field: 'totalLearners', headerName: 'Learners', minWidth: 110 },
    { field: 'viewed', headerName: 'Viewed', minWidth: 110 },
    { field: 'viewRate', headerName: 'Viewed %', minWidth: 120 },
    { field: 'completed', headerName: 'Completed', minWidth: 120 },
    { field: 'completionRate', headerName: 'Completion %', minWidth: 140 },
  ];

  const assessmentColumns = [
    { field: 'title', headerName: 'Assessment', minWidth: 200, flex: 1 },
    { field: 'type', headerName: 'Type', minWidth: 110 },
    { field: 'subject', headerName: 'Subject', minWidth: 140 },
    { field: 'grade', headerName: 'Grade', minWidth: 90 },
    { field: 'section', headerName: 'Section', minWidth: 120 },
    { field: 'submissionCount', headerName: 'Submissions', minWidth: 120 },
    { field: 'submissionRate', headerName: 'Submission %', minWidth: 140 },
    { field: 'averageScore', headerName: 'Avg Score', minWidth: 120 },
  ];

  const atRiskColumns = [
    {
      field: 'studentName',
      headerName: 'Student',
      minWidth: 200,
      valueGetter: (params) => `${params.row.student?.name || ''} ${params.row.student?.surname || ''}`.trim(),
    },
    { field: 'studentId', headerName: 'Student ID', minWidth: 220 },
    { field: 'grade', headerName: 'Grade', minWidth: 90 },
    { field: 'attendanceAvg', headerName: 'Attendance %', minWidth: 140 },
    { field: 'submissionRate', headerName: 'Submission %', minWidth: 140 },
    { field: 'averageScore', headerName: 'Avg Score', minWidth: 120 },
  ];

  return (
    <EducatorLayout>
      <Box>
        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        <Stack spacing={3}>
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight={700}>
              Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track lesson engagement, assessment submissions, and at-risk learners.
            </Typography>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardHeader title="Lesson engagement" />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="h4" fontWeight={700}>
                      {lessonEngagement.summary?.viewRate ?? 0}% viewed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {lessonEngagement.summary?.completionRate ?? 0}% completed across {lessonEngagement.summary?.totalLessons ?? 0} lessons.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardHeader title="Assessment submissions" />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="h4" fontWeight={700}>
                      {assessmentStats.summary?.submissionRate ?? 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg score {assessmentStats.summary?.averageScore ?? 0}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={1} sx={{ borderRadius: 3 }}>
                <CardHeader title="At-risk learners" />
                <CardContent>
                  <Stack spacing={1}>
                    <Typography variant="h4" fontWeight={700}>
                      {atRisk.summary?.atRiskCount ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Learners below attendance or submission thresholds.
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Lesson engagement"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  href={buildDownloadUrl('/educator-analytics/exports/lesson-engagement.csv')}
                >
                  Export CSV
                </Button>
              }
            />
            <CardContent>
              <DataGrid
                autoHeight
                rows={(lessonEngagement.rows || []).map((row) => ({ id: row.lessonId, ...row }))}
                columns={lessonColumns}
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                disableRowSelectionOnClick
              />
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Assessment submissions"
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  href={buildDownloadUrl('/educator-analytics/exports/assessment-stats.csv')}
                >
                  Export CSV
                </Button>
              }
            />
            <CardContent>
              <DataGrid
                autoHeight
                rows={(assessmentStats.rows || []).map((row) => ({ id: row.assessmentId, ...row }))}
                columns={assessmentColumns}
                pageSizeOptions={[5, 10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                disableRowSelectionOnClick
              />
            </CardContent>
          </Card>

          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardHeader
              title="At-risk learners"
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadRoundedIcon />}
                    href={buildDownloadUrl('/educator-analytics/exports/at-risk.csv')}
                  >
                    Export CSV
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<DownloadRoundedIcon />}
                    href={buildDownloadUrl('/educator-analytics/report.pdf')}
                  >
                    Export PDF
                  </Button>
                </Stack>
              }
            />
            <CardContent>
              {(atRisk.rows || []).length === 0 ? (
                <Alert severity="success">No at-risk learners flagged.</Alert>
              ) : (
                <DataGrid
                  autoHeight
                  rows={(atRisk.rows || []).map((row) => ({ id: row.studentId, ...row }))}
                  columns={atRiskColumns}
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  disableRowSelectionOnClick
                />
              )}
            </CardContent>
          </Card>

          {lessonEngagement.rows?.length === 0 && (
            <Chip label="No lesson engagement data yet." />
          )}
        </Stack>
      </Box>
    </EducatorLayout>
  );
};

export default EducatorAnalytics;
